/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import breakpoints from "assets/theme/base/breakpoints";

// Data
// import authorsTableData from "layouts/tables/data/authorsTableData";
import projectsTableData from "layouts/tables/data/projectsTableData";
// import tutorTableData from "Views/people/tutor/data/tutorTableData";
// import TestUserTableData from "Views/TestUser/data/TestUserTableData"

// import IncomeBreakDownTabelData from "Views/Report/IncomeBreakDown/data/IncomeBreakDownTableData"

// import IncomeOverTimeTableData from "Views/Report/IncomeOverTime/data/IncomeOverTimeTableData"
import { useState, useEffect, useMemo } from "react";
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import dayjs from "dayjs";
import CustomDatePicker from "components/CustomDatePicker/CustomDatePicker";
import ArgonButton from "components/ArgonButton";
import { height, width } from "@mui/system";
import { getIncomeOverTime } from "services/financeService";
import { toast } from "react-toastify";

function Tables() {
  // Generate dynamic table data from incomeData
  const generateTableData = (data) => {
    const columns = [
      { name: "sno", align: "center" },
      { name: "Month", align: "left" },
      { name: "Total Sales", align: "left" },
      { name: "Net Sales", align: "center" },
      { name: "Salary", align: "center" },
    ];

    const rows = data.map((item, index) => ({
      sno: (
        <ArgonTypography variant="caption" fontWeight="medium" color="text">
          {index + 1}
        </ArgonTypography>
      ),
      Month: (
        <ArgonTypography variant="caption" fontWeight="medium" color="text">
          {formatPeriod(item.period)}
        </ArgonTypography>
      ),
      "Total Sales": (
        <ArgonTypography variant="caption" color="success" fontWeight="medium">
          ₹{item.totalSales.toLocaleString()}
        </ArgonTypography>
      ),
      "Net Sales": (
        <ArgonTypography variant="caption" color="info" fontWeight="medium">
          ₹{item.netSales.toLocaleString()}
        </ArgonTypography>
      ),
      Salary: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          ₹{item.salary.toLocaleString()}
        </ArgonTypography>
      ),
    }));

    return { columns, rows };
  };

  // Format period display
  const formatPeriod = (period) => {
    if (currentPeriod === "daily") {
      return dayjs(period).format("DD MMM YYYY");
    } else if (currentPeriod === "monthly") {
      const [year, month] = period.split("-");
      return dayjs(`${year}-${month}-01`).format("MMM YYYY");
    } else if (currentPeriod === "quarterly") {
      const [year, quarter] = period.split("-Q");
      return `Q${quarter} ${year}`;
    } else if (currentPeriod === "yearly") {
      return period;
    }
    return period;
  };

  const tabsData = [
    { label: "Last 7 day", content: "" },
    { label: "Monthly", content: "" },
    { label: "Quarterly", content: "" },
    { label: "Yearly", content: "" },
  ];

  const [incomeData, setIncomeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPeriod, setCurrentPeriod] = useState("daily");
  const [activeTabLabel, setActiveTabLabel] = useState("Last 7 day");
  const [fromDate, setFromDate] = useState(dayjs().subtract(6, "day"));
  const [toDate, setToDate] = useState(dayjs());

  const { columns, rows } = useMemo(() => generateTableData(incomeData), [incomeData]);

  // Fetch income data
  const fetchIncomeData = async (
    period = currentPeriod,
    customFromDate = fromDate,
    customToDate = toDate
  ) => {
    setLoading(true);
    try {
      // Set limit based on period
      let limit = 12;
      if (period === "daily") limit = 7;
      else if (period === "monthly") limit = 12;
      else if (period === "quarterly") limit = 8;
      else if (period === "yearly") limit = 5;

      const params = {
        period: period,
        limit: limit,
      };

      // Add date range if custom dates are selected
      if (customFromDate && customToDate) {
        params.from = customFromDate.format("YYYY-MM-DD");
        params.to = customToDate.format("YYYY-MM-DD");
      }

      const response = await getIncomeOverTime(params);
      if (response.success) {
        setIncomeData(response.data || []);
      } else {
        toast.error(response.message || "Failed to fetch income data");
        setIncomeData([]);
      }
    } catch (error) {
      console.error("Error fetching income data:", error);
      toast.error("Failed to fetch income data");
      setIncomeData([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (tabLabel) => {
    let period = "monthly";
    let newFromDate = fromDate;
    let newToDate = toDate;

    switch (tabLabel) {
      case "Last 7 day":
        period = "daily";
        newFromDate = dayjs().subtract(6, "day");
        newToDate = dayjs();
        break;
      case "Monthly":
        period = "monthly";
        newFromDate = dayjs().subtract(11, "month");
        newToDate = dayjs();
        break;
      case "Quarterly":
        period = "quarterly";
        newFromDate = dayjs().subtract(2, "year");
        newToDate = dayjs();
        break;
      case "Yearly":
        period = "yearly";
        newFromDate = dayjs().subtract(4, "year");
        newToDate = dayjs();
        break;
      default:
        period = "monthly";
        newFromDate = dayjs().subtract(11, "month");
        newToDate = dayjs();
    }

    setCurrentPeriod(period);
    setActiveTabLabel(tabLabel);
    setFromDate(newFromDate);
    setToDate(newToDate);
    fetchIncomeData(period, newFromDate, newToDate);
  };

  // Handle filter button click
  const handleFilter = () => {
    if (!fromDate || !toDate) {
      toast.error("Please select both From and To dates");
      return;
    }
    if (fromDate.isAfter(toDate)) {
      toast.error("From date cannot be after To date");
      return;
    }
    fetchIncomeData(currentPeriod, fromDate, toDate);
  };

  useEffect(() => {
    fetchIncomeData("daily", dayjs().subtract(6, "day"), dayjs());
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                Income Over Time
              </ArgonTypography>

              <ArgonBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  height: "36px",
                  mb: 2,
                }}
              >
                <ArgonBox sx={{ flex: "0 1 auto", minWidth: 250, maxWidth: 400 }}>
                  <ApnaTabs
                    tabsData={tabsData}
                    onTabChange={handleTabChange}
                    activeTab={activeTabLabel}
                  />
                </ArgonBox>
                <ArgonBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    justifyContent: "flex-end",
                    flex: "0 1 auto",
                    minWidth: 250,
                    maxWidth: 400,
                  }}
                >
                  <CustomDatePicker
                    label="From"
                    value={fromDate}
                    onChange={(newValue) => setFromDate(newValue)}
                    width={150}
                    maxDate={dayjs()} // future date disable
                  />
                  <CustomDatePicker
                    label="To"
                    value={toDate}
                    onChange={(newValue) => setToDate(newValue)}
                    width={150}
                    maxDate={dayjs()} // future date disable
                    minDate={fromDate} // to date should be after from date
                  />
                  <ArgonButton
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ marginLeft: "10px" }}
                    onClick={handleFilter}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Filter"}
                  </ArgonButton>
                </ArgonBox>
              </ArgonBox>
              {loading && (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={4}>
                  <ArgonTypography variant="h6">Loading income data...</ArgonTypography>
                </ArgonBox>
              )}
              {!loading && incomeData.length > 0 && (
                <ArgonBox
                  sx={{
                    "& .MuiTableRow-root:not(:last-child)": {
                      "& td": {
                        borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                          `${borderWidth[1]} solid ${borderColor}`,
                      },
                    },
                  }}
                >
                  <Table columns={columns} rows={rows} />
                </ArgonBox>
              )}
              {!loading && incomeData.length === 0 && (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={4}>
                  <ArgonTypography variant="h6" color="textSecondary">
                    No income data available for the selected period
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
